<template>
  <div class="space-y-16 mb-32">
    <!-- 헤더 -->
    <div class="flex items-center justify-between p-8">
      <div>
        <h3 class="text-4xl font-bold text-white mb-2">{{ store.companyName || '자산운용사' }}</h3>
        <p class="text-gray-400 text-sm">분석일시 • {{ formatDatetime(store.analysisDatetime) }}</p>
      </div>
      <div class="inline-block">
        <div class="flex justify-end items-end gap-3">
          <span class="text-xl">
            <i class="bi bi-award-fill text-yellow-600"></i>
          </span>
          <span class="text-gray-400 text-base font-bold">종합 평가 점수</span>
          <span class="text-6xl font-bold text-secondary">{{ store.totalScore || 0 }}</span>
          <span class="text-2xl text-gray-400">/ 10.0</span>
        </div>
      </div>
    </div>

    <!-- 탭 네비게이션 -->
    <div>
      <div class="flex justify-center border-t-2 border-b-2 border-dark-600 overflow-x-auto mb-6">
        <button
          v-for="tab in mainTabs"
          :key="tab.key"
          :class="[
            'px-6 py-4 font-medium transition-colors duration-200 border-b-2 whitespace-nowrap',
            selectedMainTab === tab.key
              ? 'text-primary border-primary bg-dark-700'
              : 'text-gray-400 border-transparent hover:text-white'
          ]"
          @click="selectedMainTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- 탭 컨텐츠 -->
      <div class="p-3 pt-4">
        <!-- TAB: 영업보고서 -->
        <div v-show="selectedMainTab === 'report'">

          <!-- SECTION 1: 재무 정보 -->
          <div class="space-y-6">
            <h4 class="text-heading-2 font-semibold text-white pl-4">
              <i class="bi bi-1-square mr-2"></i>
              재무 정보
            </h4>

            <!-- 재무 테이블 -->
            <div v-if="quarterlyData && quarterlyData.length > 0" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-graph-up-arrow mr-2"></i>
                분기별 상세 정보
              </h4>
              <div class="bg-dark-700 rounded-lg overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead class="bg-dark-600">
                      <tr>
                        <th class="px-4 py-3 text-left text-gray-300 font-semibold">구분</th>
                        <th 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year"
                          class="px-4 py-3 text-right text-gray-300 font-semibold whitespace-nowrap"
                        >
                          {{ quarter.business_year }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-dark-600">
                      <tr class="hover:bg-dark-600/50 transition-colors">
                        <td class="px-4 py-3 text-white font-medium">자산총계</td>
                        <td 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year + '_assets'"
                          class="px-4 py-3 text-right text-gray-300"
                        >
                          {{ formatNumber(quarter.financial.total_assets) }}
                        </td>
                      </tr>
                      <tr class="hover:bg-dark-600/50 transition-colors">
                        <td class="px-4 py-3 text-white font-medium">자본총계</td>
                        <td 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year + '_capital'"
                          class="px-4 py-3 text-right text-gray-300"
                        >
                          {{ formatNumber(quarter.financial.total_capital) }}
                        </td>
                      </tr>
                      <tr class="hover:bg-dark-600/50 transition-colors">
                        <td class="px-4 py-3 text-white font-medium">당기순이익</td>
                        <td 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year + '_profit'"
                          class="px-4 py-3 text-right text-primary"
                        >
                          {{ formatNumber(quarter.financial.net_profit) }}
                        </td>
                      </tr>
                      <tr class="hover:bg-dark-600/50 transition-colors">
                        <td class="px-4 py-3 text-white font-medium">영업수익</td>
                        <td 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year + '_revenue'"
                          class="px-4 py-3 text-right text-secondary"
                        >
                          {{ formatNumber(quarter.financial.operating_revenue) }}
                        </td>
                      </tr>
                      <tr class="hover:bg-dark-600/50 transition-colors">
                        <td class="px-4 py-3 text-white font-medium">운용자산(AUM)</td>
                        <td 
                          v-for="quarter in quarterlyData" 
                          :key="quarter.business_year + '_aum'"
                          class="px-4 py-3 text-right text-accent"
                        >
                          {{ formatNumber(quarter.financial.aum) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>
            </div>

            <!-- 재무 차트 -->
            <div v-if="quarterlyData && quarterlyData.length > 0" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-graph-up-arrow mr-2"></i>
                분기별 추이 분석
              </h4>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <!-- 자산총계 & 자본총계 통합 차트 -->
                <div class="lg:col-span-2">
                  <MultiMetricLineChart
                    title="자산총계 & 자본총계"
                    subtitle="자산 및 자본 추이 비교"
                    :data="quarterlyData"
                    :metrics="[
                      { key: 'total_assets', label: '자산총계', color: '#22d3ee' },
                      { key: 'total_capital', label: '자본총계', color: '#3b82f6' }
                    ]"
                  />
                </div>

                <!-- 순이익 차트 -->
                <SingleMetricLineChart
                  title="당기순이익"
                  subtitle="당기순이익 추이"
                  :data="quarterlyData"
                  metric-key="net_profit"
                  color="#86efac"
                  :show-three-year-average="true"
                  :show-yo-y-layer="true"
                />

                <!-- 영업수익 차트 -->
                <SingleMetricLineChart
                  title="영업수익"
                  subtitle="영업수익 추이"
                  :data="quarterlyData"
                  metric-key="operating_revenue"
                  color="#059669"
                  :show-three-year-average="true"
                  :show-yo-y-layer="true"
                />

                <!-- 운용자산 차트 (전체 너비) -->
                <div class="lg:col-span-2">
                  <SingleMetricLineChart
                    title="운용자산(AUM)"
                    subtitle="AUM 추이"
                    :data="quarterlyData"
                    metric-key="aum"
                    color="#8b5cf6"
                  />
                </div>
              </div>
            </div>

            <!-- 재무 데이터 없음 -->
            <div v-else class="bg-dark-700 rounded-lg p-12">
              <div class="text-center">
                <i class="bi bi-inbox text-6xl text-gray-400"></i>
                <p class="text-gray-400 text-lg pt-4">재무 정보가 없습니다.</p>
              </div>
            </div>
          </div>

          <!-- SECTION 2: 주주 현황 -->
          <div class="space-y-6 mt-24">
            <h4 class="text-heading-2 font-semibold text-white pl-4">
              <i class="bi bi-2-square mr-2"></i>
              주주 현황
            </h4>

            <!-- 현재 주주 -->
            <div v-if="latestQuarterData && latestQuarterData.shareholders && latestQuarterData.shareholders.length > 0" class="bg-dark-700 rounded-lg p-6">
              <div class="flex items-center justify-between mb-8 border-b border-dark-600 pb-4">
                <div>
                  <h4 class="text-lg font-semibold text-white mb-2">
                    <i class="bi bi-person-fill mr-2"></i>
                    현재 주주 정보
                  </h4>
                  <p class="text-sm text-gray-400">
                    {{ latestQuarterData.business_year }} 기준 • 총 {{ latestQuarterData.shareholders.length }}명
                  </p>
                </div>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-dark-600">
                    <tr>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">주주명</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">관계</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">주식종류</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">주식수</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">지분율</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold">구분</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-dark-600">
                    <tr 
                      v-for="(shareholder, idx) in latestQuarterData.shareholders" 
                      :key="idx"
                      class="hover:bg-dark-600/50 transition-colors"
                    >
                      <td class="px-4 py-3 text-center">
                        <span class="text-white font-medium">{{ shareholder.name }}</span>
                      </td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ shareholder.relation || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ shareholder.stock_type || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ formatNumber(shareholder.end_stock) }}</td>
                      <td class="px-4 py-3 text-center">
                        <span class="text-primary font-bold text-lg">{{ shareholder.ownership }}%</span>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span 
                          :class="[
                            'text-xs px-3 py-1 rounded',
                            shareholder.shareholder_type === '최대주주' 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-secondary/20 text-secondary'
                          ]"
                        >
                          {{ shareholder.shareholder_type }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 주주 추이 -->
            <div v-if="quarterlyData && quarterlyData.length > 0" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-graph-up-arrow mr-2"></i>
                주주 구성 변화
              </h4>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-dark-600">
                    <tr>
                      <th class="px-4 py-3 text-left text-gray-300 font-semibold">주주명</th>
                      <th class="px-4 py-3 text-left text-gray-300 font-semibold">관계</th>
                      <th class="px-4 py-3 text-left text-gray-300 font-semibold">주주유형</th>
                      <th 
                        v-for="quarter in quarterlyData" 
                        :key="quarter.business_year"
                        class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap"
                      >
                        {{ quarter.business_year }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-dark-600">
                    <tr 
                      v-for="(shareholder, idx) in allShareholders" 
                      :key="idx"
                      class="hover:bg-dark-600/50 transition-colors"
                    >
                      <td class="px-4 py-3 text-white font-medium">{{ shareholder.name }}</td>
                      <td class="px-4 py-3 text-gray-300">{{ shareholder.relation }}</td>
                      <td class="px-4 py-3">
                        <span 
                          :class="[
                            'text-xs px-3 py-1 rounded',
                            shareholder.shareholder_type === '최대주주' 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-secondary/20 text-secondary'
                          ]"
                        >
                          {{ shareholder.shareholder_type }}
                        </span>
                      </td>
                      <td 
                        v-for="quarter in quarterlyData" 
                        :key="quarter.business_year + '_' + shareholder.name"
                        class="px-4 py-3 text-center"
                      >
                        <div v-if="getShareholderData(quarter, shareholder.name)">
                          <p class="text-white font-bold">
                            {{ getShareholderData(quarter, shareholder.name).ownership }}%
                          </p>
                          <p class="text-xs text-gray-400 mt-1">
                            {{ formatNumber(getShareholderData(quarter, shareholder.name).end_stock) }} 주
                          </p>
                        </div>
                        <div v-else class="text-gray-500">-</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 주주 데이터 없음 -->
            <div v-if="!latestQuarterData || !latestQuarterData.shareholders || latestQuarterData.shareholders.length === 0" class="bg-dark-700 rounded-lg p-12">
              <div class="text-center">
                <i class="bi bi-inbox text-6xl text-gray-400"></i>
                <p class="text-gray-400 text-lg pt-4">주주 현황이 없습니다.</p>
              </div>
            </div>
          </div>

          <!-- SECTION 3: 투자운용인력 -->
          <div class="space-y-6 mt-24">
            <h4 class="text-heading-2 font-semibold text-white pl-4">
              <i class="bi bi-3-square mr-2"></i>
              투자운용인력 현황
            </h4>

            <!-- 주주인 인력 -->
            <div v-if="personnelShareholders.length > 0" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-star-fill mr-2 text-yellow-400"></i>
                투자운용인력 중 주주 현황
                <p class="text-sm text-gray-400 mt-2">
                  총 {{ personnelShareholders.length }}명 • 합산 지분율 {{ totalShareholderOwnership }}%
                </p>
              </h4>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-dark-600">
                    <tr>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[10%]">이름</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[10%]">직위</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[10%]">담당업무</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[10%]">주주유형</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[10%]">지분율</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold w-[50%]">경력</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-dark-600">
                    <tr 
                      v-for="(person, idx) in personnelShareholders" 
                      :key="idx"
                      class="hover:bg-dark-600/50 transition-colors"
                    >
                      <td class="px-4 py-3 text-center text-white font-medium">{{ person.name }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.position }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.duty }}</td>
                      <td class="px-4 py-3 text-center">
                        <span class="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                          {{ person.shareholder_type }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span class="text-primary font-bold text-lg">{{ person.ownership }}%</span>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span class="text-sm px-2 py-1">
                          {{ person.career }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 인력 변동 -->
            <div v-if="store.movementStatistics" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-graph-up mr-2"></i>
                인력 변동 통계
              </h4>
              
              <!-- 통계 카드 (1행에 3개) -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div class="bg-dark-600 rounded-lg p-6 px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm text-gray-400">현재 인력</p>
                    <i class="bi bi-people text-2xl text-white"></i>
                  </div>
                  <p class="text-4xl font-bold text-white mt-2">{{ store.movementStatistics.current_personnel_count }}</p>
                </div>
                
                <div class="bg-dark-600 rounded-lg p-6 px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm text-gray-400">총 신규 입사</p>
                    <i class="bi bi-person-plus text-2xl text-primary"></i>
                  </div>
                  <p class="text-4xl font-bold text-primary mt-2">{{ store.movementStatistics.new_entries }}</p>
                </div>
                
                <div class="bg-dark-600 rounded-lg p-6 px-6">
                  <div class="flex items-center justify-between">
                    <p class="text-sm text-gray-400">총 퇴사</p>
                    <i class="bi bi-person-dash text-2xl text-red-400"></i>
                  </div>
                  <p class="text-4xl font-bold text-red-400 mt-2">{{ store.movementStatistics.resignations }}</p>
                </div>
              </div>

              <!-- 차트 (별도 행) -->
              <div v-if="movementByPeriod && movementByPeriod.length > 0">
                <PersonnelMovementChart
                  title="분기별 인력 변동"
                  subtitle="신규 입사 및 퇴사 추이"
                  :movement-data="movementByPeriod"
                />
              </div>
            </div>

            <!-- 현재 인력 -->
            <div v-if="store.currentPersonnel && store.currentPersonnel.length > 0" class="bg-dark-700 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                <i class="bi bi-person-badge-fill mr-2"></i>
                현재 운용인력 ({{ store.currentPersonnel.length }}명)
              </h4>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-dark-600">
                    <tr>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">이름</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">직위</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">담당업무</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">경력</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">자격증</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">학력</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">계약수</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">운용규모</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">주주여부</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">협회등록일자</th>
                      <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap">상근여부</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-dark-600">
                    <tr 
                      v-for="(person, index) in store.currentPersonnel" 
                      :key="index"
                      class="hover:bg-dark-600/50 transition-colors"
                    >
                      <td class="px-4 py-3 text-center text-white font-medium">{{ person.name || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.position || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.duty || '-' }}</td>
                      <td class="px-4 py-3 text-left text-gray-300 whitespace-normal break-words min-w-[200px] max-w-[400px]">
                        {{ person.career || '-' }}
                      </td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.certificate || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.education || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.contract_count || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">
                        <span v-if="person.aum">{{ formatNumber(person.aum) }}</span>
                        <span v-else class="text-gray-500">-</span>
                      </td>
                      <td class="px-4 py-3 text-center">
                        <div v-if="person.ownership">
                          <span class="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                            {{ person.shareholder_type || '주주' }} ({{ person.ownership }}%)
                          </span>
                        </div>
                        <span v-else class="text-gray-500">-</span>
                      </td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.registration_date || '-' }}</td>
                      <td class="px-4 py-3 text-center text-gray-300">{{ person.resident_status || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 입사/퇴사 인력 -->
            <div class="space-y-6">
              <!-- 신규 입사 -->
              <div v-if="newPersonnelDetails.length > 0" class="bg-dark-700 rounded-lg p-6">
                <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                  <i class="bi bi-person-plus-fill mr-2 text-primary"></i>
                  신규 입사 인력 ({{ newPersonnelDetails.length }}명)
                </h4>
                <div class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead class="bg-dark-600">
                      <tr>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">이름</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">직위</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">담당업무</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">입사 분기</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-dark-600">
                      <tr 
                        v-for="(person, idx) in newPersonnelDetails" 
                        :key="idx"
                        class="hover:bg-dark-600/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-center text-white font-medium">{{ person.name }}</td>
                        <td class="px-4 py-3 text-center text-gray-300">{{ person.position }}</td>
                        <td class="px-4 py-3 text-center text-gray-300">{{ person.duty }}</td>
                        <td class="px-4 py-3 text-center text-gray-300">{{ person.entry_period || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- 퇴사 -->
              <div v-if="leftPersonnelDetails.length > 0" class="bg-dark-700 rounded-lg p-6">
                <h4 class="text-lg font-semibold text-white mb-8 border-b border-dark-600 pb-4">
                  <i class="bi bi-person-dash-fill mr-2 text-red-400"></i>
                  퇴사 인력 ({{ leftPersonnelDetails.length }}명)
                </h4>
                <div class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead class="bg-dark-600">
                      <tr>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">이름</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">직위</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">담당업무</th>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold">퇴사 분기</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-dark-600">
                      <tr 
                        v-for="(person, idx) in leftPersonnelDetails" 
                        :key="idx"
                        class="hover:bg-dark-600/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-center text-white font-medium">{{ person.name }}</td>
                        <td class="px-4 py-3 text-center text-gray-300">{{ person.position }}</td>
                        <td class="px-4 py-3 text-center text-gray-300">{{ person.duty }}</td>
                        <td class="px-4 py-3 text-center text-red-400">{{ person.exit_period || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- 인력 데이터 없음 -->
            <div v-if="!store.currentPersonnel || store.currentPersonnel.length === 0" class="bg-dark-700 rounded-lg p-12">
              <div class="text-center">
                <i class="bi bi-inbox text-6xl text-gray-400"></i>
                <p class="text-gray-400 text-lg pt-4">투자운용인력 현황이 없습니다.</p>
              </div>
            </div>
          </div>

          <!-- SECTION 4: 제재 현황 -->
          <div class="space-y-6 mt-24">
            <h4 class="text-heading-2 font-semibold text-white pl-4">
              <i class="bi bi-4-square mr-2"></i>
              제재 현황
            </h4>

            <div v-if="sanctionsByQuarter.length > 0" class="space-y-8">
              <div 
                v-for="(quarterGroup, quarterIndex) in sanctionsByQuarter" 
                :key="quarterIndex"
                class="bg-dark-700 rounded-lg p-6"
              >
                <!-- 분기 헤더 -->
                <div class="flex items-center justify-between mb-6 border-b border-dark-600 pb-4">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-calendar3 text-primary text-lg"></i>
                    <h5 class="text-lg font-semibold text-white">{{ quarterGroup.business_year }}</h5>
                    <span class="px-3 rounded-full bg-red-500/20 border border-red-500/30">
                      <span class="text-red-400 font-bold text-sm">{{ quarterGroup.sanctions.length }}건</span>
                    </span>
                  </div>
                </div>

                <!-- 제재 테이블 -->
                <div class="overflow-x-auto">
                  <table class="w-full text-sm table-fixed">
                    <thead class="bg-dark-600">
                      <tr>
                        <th class="px-4 py-3 text-center text-gray-300 font-semibold whitespace-nowrap w-[13%]">제재 일자</th>
                        <th class="px-4 py-3 text-left text-gray-300 font-semibold w-[55%]">제재 원인</th>
                        <th class="px-4 py-3 text-left text-gray-300 font-semibold">제재 내용</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-dark-600">
                      <tr 
                        v-for="(sanction, sanctionIndex) in quarterGroup.sanctions" 
                        :key="sanctionIndex"
                        class="hover:bg-dark-600/50 transition-colors"
                      >
                        <td class="px-4 py-3 text-center text-white font-medium whitespace-nowrap w-[13%]">
                          {{ formatDate(sanction.sanction_date || '') }}
                        </td>
                        <td class="px-4 py-3 text-gray-300 w-[55%]">
                          {{ sanction.sanction_reason || '-' }}
                        </td>
                        <td class="px-4 py-3 text-gray-300">
                          {{ sanction.sanction_content || '-' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div v-else class="bg-dark-700 rounded-lg p-12">
              <div class="text-center">
                <i class="bi bi-check2-circle text-4xl text-gray-400"></i>
                <p class="text-gray-400 text-lg pt-3">제재 현황이 없습니다.</p>
              </div>
            </div>
          </div>
        </div>

<!-- TAB 2: 종합 의견 -->
        <div v-show="selectedMainTab === 'summary'" class="space-y-12">
        <div v-if="store.financialComponents">
          <div class="bg-dark-700 border border-dark-600 rounded-2xl p-8 shadow-[0_20px_45px_rgba(0,0,0,0.35)] space-y-8">
            <!-- 재무적 평가 요약 -->
            <div class="flex flex-col gap-5">
              <div class="flex flex-wrap items-center justify-between gap-4 bg-dark-800/70 border-4 border-primary/30 rounded-xl px-8 py-5 shadow-[0_12px_25px_rgba(0,0,0,0.35)]">
                <div class="flex items-center gap-4">
                  <i class="bi bi-graph-up text-primary text-2xl"></i>
                  <div>
                    <p class="text-caption uppercase tracking-[0.2em] text-primary mb-2">Financial Overview</p>
                    <h4 class="text-2xl font-semibold text-white">재무적 요소 종합</h4>
                  </div>
                </div>
                <span class="text-3xl font-bold text-primary">{{ store.financialOverallScore }}/10</span>
              </div>
              <p class="text-gray-300 text-base leading-relaxed">
                {{ store.financialScoreDescription }}
              </p>
            </div>

            <!-- 재무 세부 카드 -->
            <div class="grid grid-cols-1 gap-6">
              <!-- 자산 평가 -->
              <div
                v-if="store.financialComponents.assets"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-bank text-primary text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">자산</h5>
                  </div>
                  <span class="text-2xl font-bold text-primary">{{ store.financialComponents.assets.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.financialComponents.assets.score_rationale }}</p>
                <div
                  v-if="store.financialComponents.assets.metrics"
                  class="grid grid-cols-2 gap-3 pt-2"
                >
              <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">총자산</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.assets.metrics.current_total_assets }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">자본비율</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.assets.metrics.equity_to_assets_ratio }}</p>
                  </div>
              <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전분기 대비(QoQ) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.assets.metrics.qoq_growth_rate }}</p>
                  </div>
              <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전년 대비(YoY) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.assets.metrics.yoy_growth_rate }}</p>
                  </div>
                </div>
              </div>

              <!-- 자본 평가 -->
              <div
                v-if="store.financialComponents.capital"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-safe text-primary text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">자본</h5>
                  </div>
                  <span class="text-2xl font-bold text-primary">{{ store.financialComponents.capital.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.financialComponents.capital.score_rationale }}</p>
                <div
                  v-if="store.financialComponents.capital.metrics"
                  class="grid grid-cols-2 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">총자본</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.capital.metrics.current_total_equity }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">자본비율</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.capital.metrics.capital_ratio_equity_over_assets }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전분기 대비(QoQ) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.capital.metrics.qoq_growth_rate }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전년 대비(YoY) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.capital.metrics.yoy_growth_rate }}</p>
                  </div>
                </div>
              </div>

              <!-- 수익성 평가 -->
              <div
                v-if="store.financialComponents.profitability"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-currency-exchange text-primary text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">수익성</h5>
                  </div>
                  <span class="text-2xl font-bold text-primary">{{ store.financialComponents.profitability.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.financialComponents.profitability.score_rationale }}</p>
                <div
                  v-if="store.financialComponents.profitability.metrics"
                  class="grid grid-cols-2 lg:grid-cols-5 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">순이익</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.profitability.metrics.current_net_income }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">총자산이익률(ROA)</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.profitability.metrics.estimated_roa }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">자기자본이익률(ROE)</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.profitability.metrics.estimated_roe }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전분기 대비(QoQ)</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.profitability.metrics.qoq_growth_rate }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전년 대비(YoY)</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.profitability.metrics.yoy_growth_rate }}</p>
                  </div>
                </div>
              </div>

              <!-- 영업수익 평가 -->
              <div
                v-if="store.financialComponents.operating_revenue"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-bar-chart text-primary text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">영업수익</h5>
                  </div>
                  <span class="text-2xl font-bold text-primary">{{ store.financialComponents.operating_revenue.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.financialComponents.operating_revenue.score_rationale }}</p>
                <div
                  v-if="store.financialComponents.operating_revenue.metrics"
                  class="grid grid-cols-2 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">영업수익</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.operating_revenue.metrics.current_operating_revenue }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전분기 대비(QoQ) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.operating_revenue.metrics.qoq_growth_rate }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">전년 대비(YoY) 성장률</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.operating_revenue.metrics.yoy_growth_rate }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">변동계수(CV)</p>
                    <p class="text-base font-semibold text-white">{{ store.financialComponents.operating_revenue.metrics.revenue_volatility_cv }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="store.nonFinancialComponents">
          <div class="bg-dark-700 border border-dark-600 rounded-2xl p-8 shadow-[0_20px_45px_rgba(0,0,0,0.35)] space-y-8">
            <!-- 비재무적 평가 요약 -->
            <div class="flex flex-col gap-5">
              <div class="flex flex-wrap items-center justify-between gap-4 bg-dark-800/70 border-4 border-accent/30 rounded-xl px-8 py-5 shadow-[0_12px_25px_rgba(0,0,0,0.35)]">
                <div class="flex items-center gap-4">
                  <i class="bi bi-people text-accent text-2xl"></i>
                  <div>
                    <p class="text-caption uppercase tracking-[0.2em] text-accent mb-2">Non-Financial Overview</p>
                    <h4 class="text-2xl font-semibold text-white">비재무적 요소 종합</h4>
                  </div>
                </div>
                <span class="text-3xl font-bold text-accent">{{ store.nonFinancialOverallScore }}/10</span>
              </div>
              <p class="text-gray-300 text-base leading-relaxed">
                {{ store.nonFinancialScoreDescription }}
              </p>
            </div>

            <div class="grid grid-cols-1 gap-6">
              <!-- 주주 안정성 -->
              <div
                v-if="store.nonFinancialComponents.shareholder_stability"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-diagram-3 text-accent text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">주주 안정성</h5>
                  </div>
                  <span class="text-2xl font-bold text-accent">{{ store.nonFinancialComponents.shareholder_stability.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.nonFinancialComponents.shareholder_stability.score_rationale }}</p>
                <div
                  v-if="store.nonFinancialComponents.shareholder_stability.metrics"
                  class="grid grid-cols-2 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">최대주주 지분율</p>
                    <p class="text-base font-semibold text-white">{{ store.nonFinancialComponents.shareholder_stability.metrics.largest_shareholder_pct }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">특수관계인 지분율</p>
                    <p class="text-base font-semibold text-white">{{ store.nonFinancialComponents.shareholder_stability.metrics.related_parties_pct }}</p>
                  </div>
                </div>
              </div>

              <!-- 인력 안정성 -->
              <div
                v-if="store.nonFinancialComponents.personnel_stability"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-person-lines-fill text-accent text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">인력 안정성</h5>
                  </div>
                  <span class="text-2xl font-bold text-accent">{{ store.nonFinancialComponents.personnel_stability.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.nonFinancialComponents.personnel_stability.score_rationale }}</p>
                <div
                  v-if="store.nonFinancialComponents.personnel_stability.metrics"
                  class="grid grid-cols-3 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">현재 운용인력</p>
                    <p class="text-base font-semibold text-white">{{ store.nonFinancialComponents.personnel_stability.metrics.current_investment_staff }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">신규 입사</p>
                    <p class="text-base font-semibold text-primary">{{ store.nonFinancialComponents.personnel_stability.metrics.new_investment_staff }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">퇴사</p>
                    <p class="text-base font-semibold text-red-400">{{ store.nonFinancialComponents.personnel_stability.metrics.resigned_investment_staff }}</p>
                  </div>
                </div>
              </div>

              <!-- 규제 준수 -->
              <div
                v-if="store.nonFinancialComponents.regulatory_compliance"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <i class="bi bi-shield-check text-accent text-xl"></i>
                    <h5 class="text-lg font-semibold text-white">규제 준수</h5>
                  </div>
                  <span class="text-2xl font-bold text-accent">{{ store.nonFinancialComponents.regulatory_compliance.score }}/10</span>
                </div>
                <p class="text-gray-300 text-base leading-relaxed">{{ store.nonFinancialComponents.regulatory_compliance.score_rationale }}</p>
                <div
                  v-if="store.nonFinancialComponents.regulatory_compliance.metrics"
                  class="grid grid-cols-2 gap-3 pt-2"
                >
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">제재 건수</p>
                    <p class="text-base font-semibold text-red-400">{{ store.nonFinancialComponents.regulatory_compliance.metrics.sanctions_count }}</p>
                  </div>
                  <div class="rounded-xl p-4 border border-dark-600 bg-transparent">
                    <p class="text-sm text-gray-400 mb-1">제재 금액 합계</p>
                    <p class="text-base font-semibold text-red-400">{{ store.nonFinancialComponents.regulatory_compliance.metrics.sanctions_amount_total }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-dark-700 border border-dark-600 rounded-2xl p-8 space-y-6">
          <div class="flex items-center justify-between border-4 border-blue-400/40 rounded-xl px-8 py-5">
            <div>
              <div class="flex items-center gap-3">
                <i class="bi bi-award text-blue-400 text-2xl"></i>
                <div>
                  <p class="text-caption uppercase tracking-[0.2em] text-blue-300 mb-2">Overall Evaluation</p>
                  <h4 class="text-2xl font-semibold text-white">종합 평가 근거</h4>
                </div>
              </div>
            </div>
            <span class="text-3xl font-bold text-blue-400">{{ store.totalScore }}/10</span>
          </div>
          <p class="text-gray-300 text-base leading-relaxed">
            {{ store.overallScoreRationale || '평가 근거가 없습니다.' }}
          </p>

            <div class="space-y-10">
              <!-- 리스크 요인 -->
              <div v-if="store.riskFactors" class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="bi bi-exclamation-octagon text-red-400 text-xl"></i>
                  <h4 class="text-lg font-semibold text-white">리스크 요인</h4>
                </div>
                <p class="text-gray-300 text-base leading-relaxed whitespace-pre-line">{{ store.riskFactors }}</p>
              </div>

              <!-- 강점 -->
              <div v-if="store.strengths" class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="bi bi-star text-primary text-xl"></i>
                  <h4 class="text-lg font-semibold text-white">강점</h4>
                </div>
                <p class="text-gray-300 text-base leading-relaxed whitespace-pre-line">{{ store.strengths }}</p>
              </div>

              <!-- 권고사항 -->
              <div v-if="store.recommendations" class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="bi bi-compass text-secondary text-xl"></i>
                  <h4 class="text-lg font-semibold text-white">권고사항</h4>
                </div>
                <p class="text-gray-300 text-base leading-relaxed whitespace-pre-line">{{ store.recommendations }}</p>
              </div>

              <!-- 종합 의견 -->
              <div v-if="store.overallComment" class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6 space-y-4">
                <div class="flex items-center gap-3 mb-3">
                  <i class="bi bi-chat-dots text-accent text-xl"></i>
                  <h4 class="text-lg font-semibold text-white">종합 의견</h4>
                </div>
                <p class="text-gray-300 text-base leading-relaxed whitespace-pre-line">{{ store.overallComment }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 액션 버튼 -->
    <div class="flex gap-20 border-t-4 border-dark-600 pt-20">
      <button
        @click="scrollToTop"
        class="flex-1 btn-secondary"
      >
        <i class="bi bi-arrow-up mr-2"></i>맨 위로 올라가기
      </button>
      <button
        @click="store.reset()"
        class="flex-1 btn-primary"
      >
        <i class="bi bi-arrow-clockwise mr-2"></i>다시 분석하기
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useAssetManagementStore } from '@/stores/assetManagement';
import SingleMetricLineChart from '@/components/charts/SingleMetricLineChart.vue';
import MultiMetricLineChart from '@/components/charts/MultiMetricLineChart.vue';
import PersonnelMovementChart from '@/components/charts/PersonnelMovementChart.vue';

const store = useAssetManagementStore();
const selectedMainTab = ref('report');
const mainTabs = [
  { key: 'report', label: '자산운용사 분석' },
  { key: 'summary', label: '자산운용사 종합평가' },
];

// ---------- 재무/주주 데이터 ----------
const quarterlyData = computed(() => {
  const data = store.analysisData?.step_1_asset_report?.asset_report_info;
  if (!data || !Array.isArray(data)) return [];

  return [...data].sort((a, b) => a.business_year.localeCompare(b.business_year));
});

const latestQuarterData = computed(() => {
  if (!quarterlyData.value || quarterlyData.value.length === 0) return null;
  return quarterlyData.value[quarterlyData.value.length - 1];
});

const allShareholders = computed(() => {
  if (!quarterlyData.value || quarterlyData.value.length === 0) return [];
  const shareholderMap = new Map();

  quarterlyData.value.forEach(quarter => {
    if (quarter.shareholders && Array.isArray(quarter.shareholders)) {
      quarter.shareholders.forEach(shareholder => {
        if (!shareholderMap.has(shareholder.name)) {
          shareholderMap.set(shareholder.name, {
            name: shareholder.name,
            relation: shareholder.relation || '-',
            shareholder_type: shareholder.shareholder_type || '-'
          });
        }
      });
    }
  });
  return Array.from(shareholderMap.values());
});

const getShareholderData = (quarter, shareholderName) => {
  if (!quarter.shareholders || !Array.isArray(quarter.shareholders)) return null;
  return quarter.shareholders.find(s => s.name === shareholderName);
};

// ---------- 제재 데이터 ----------
// 분기별 제재 데이터 그룹화
const sanctionsByQuarter = computed(() => {
  if (!quarterlyData.value || quarterlyData.value.length === 0) return [];
  
  const grouped = {};
  quarterlyData.value.forEach(quarter => {
    if (quarter && quarter.sanctions && Array.isArray(quarter.sanctions) && quarter.sanctions.length > 0) {
      const validSanctions = quarter.sanctions.filter(s => s && s.sanction_date);
      if (validSanctions.length > 0) {
        grouped[quarter.business_year] = {
          business_year: quarter.business_year,
          sanctions: validSanctions.sort((a, b) => {
            const dateA = a.sanction_date || '';
            const dateB = b.sanction_date || '';
            return dateB.localeCompare(dateA);
          })
        };
      }
    }
  });
  
  // 분기순으로 정렬 (최신순)
  return Object.values(grouped).sort((a, b) => {
    return b.business_year.localeCompare(a.business_year);
  });
});

// ---------- 인력 데이터 ----------
const personnelShareholders = computed(() => {
  if (!store.currentPersonnel || !Array.isArray(store.currentPersonnel)) return [];
  return store.currentPersonnel.filter(person => person.ownership && person.ownership !== '-');
});

const totalShareholderOwnership = computed(() => {
  if (personnelShareholders.value.length === 0) return 0;
  const total = personnelShareholders.value.reduce((sum, person) => {
    const ownership = parseFloat(person.ownership) || 0;
    return sum + ownership;
  }, 0);
  return total.toFixed(1);
});

const movementByPeriod = computed(() => {
  return store.analysisData?.step_2_personnel_analysis?.personnel_info?.movement_by_period || [];
});

const newEntryNamesFromMovement = computed(() => {
  if (!movementByPeriod.value || movementByPeriod.value.length === 0) return [];
  const names = [];
  movementByPeriod.value.forEach(period => {
    if (period.new_entry && Array.isArray(period.new_entry)) {
      names.push(...period.new_entry);
    }
  });
  return names;
});

const resignationNamesFromMovement = computed(() => {
  if (!movementByPeriod.value || movementByPeriod.value.length === 0) return [];
  const names = [];
  movementByPeriod.value.forEach(period => {
    if (period.resignation && Array.isArray(period.resignation)) {
      names.push(...period.resignation);
    }
  });
  return names;
});

const newPersonnelDetails = computed(() => {
  if (!store.currentPersonnel || !Array.isArray(store.currentPersonnel)) return [];
  const newPersonnel = store.currentPersonnel.filter(person => 
    newEntryNamesFromMovement.value.includes(person.name)
  );

  return newPersonnel.map(person => {
    let entryPeriod = '';
    if (movementByPeriod.value) {
      for (const period of movementByPeriod.value) {
        if (period.new_entry && period.new_entry.includes(person.name)) {
          entryPeriod = period.period;
          break;
        }
      }
    }
    return {
      ...person,
      entry_period: entryPeriod
    };
  });
});

const leftPersonnelDetails = computed(() => {
  if (!store.leftPersonnel || !Array.isArray(store.leftPersonnel)) return [];
  const leftPersonnel = store.leftPersonnel.filter(person => 
    resignationNamesFromMovement.value.includes(person.name)
  );

  return leftPersonnel.map(person => {
    let exitPeriod = '';
    if (movementByPeriod.value) {
      for (const period of movementByPeriod.value) {
        if (period.resignation && period.resignation.includes(person.name)) {
          exitPeriod = period.period;
          break;
        }
      }
    }
    return {
      ...person,
      exit_period: exitPeriod
    };
  });
});

// ---------- 유틸 ----------
const formatDatetime = (datetime) => {
  if (!datetime) return '';
  const date = new Date(datetime.replace(' ', 'T'));
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${year}.${month}.${day}`;
};

const formatNumber = (value) => {
  if (!value) return '0';
  if (typeof value === 'string' && value.includes(',')) {
    return value;
  }
  const num = typeof value === 'string' ? parseInt(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return value;
  return num.toLocaleString('ko-KR');
};

const scrollToTop = () => {
  // Layout.vue의 main 요소가 실제 스크롤 컨테이너
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // fallback: window 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}
</style>
